import { getBackendOrigin } from './apiBridge';
import { decryptString } from '../utils/crypto';

const LOCAL_BACKEND_ORIGIN = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:3000';

export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${getBackendOrigin()}${normalized}`;
}

/** 读取 token 并自动解密（兼容 authService 的加密存储和旧版明文存储） */
function getAuthToken(): string | null {
  try {
    const raw = localStorage.getItem('xf_auth_token');
    if (!raw) return null;
    const decrypted = decryptString(raw);
    if (decrypted && decrypted.length > 0) return decrypted;
    if (raw.includes('.')) return raw;
    return null;
  } catch { return null; }
}

function withAuthHeaders(headers?: HeadersInit): HeadersInit {
  const next = new Headers(headers);
  try {
    const token = getAuthToken();
    if (token && !next.has('Authorization')) {
      next.set('Authorization', `Bearer ${token}`);
    }
  } catch (e) { console.warn('[apiClient] 读取 token 失败:', e); }
  return next;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function shouldRetryLocalBackend(url: string, error: unknown): boolean {
  if (!url.startsWith(LOCAL_BACKEND_ORIGIN)) return false;
  const message = error instanceof Error ? error.message : String(error || '');
  return /failed to fetch|networkerror|load failed|fetch/i.test(message);
}

// v3.12.10: 不同操作走不同超时，避免文件上传（可能 >5min）被 15s 硬限杀掉
const TIMEOUT_PRESETS = {
  default: 15_000,
  upload:  5 * 60_000,   // 5 minutes for file uploads
  long:    60_000,       // 1 minute for generation / analysis
} as const;

function resolveTimeoutMs(body: BodyInit | null | undefined): number {
  if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
    return TIMEOUT_PRESETS.upload;
  }
  return TIMEOUT_PRESETS.default;
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = apiUrl(path);
  const request: RequestInit = {
    credentials: 'include',
    ...init,
    headers: withAuthHeaders(init.headers),
  };
  const attempts = url.startsWith(LOCAL_BACKEND_ORIGIN) ? 3 : 1;
  let lastError: unknown;
  const timeoutMs = resolveTimeoutMs(request.body);

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...request, signal: controller.signal });
      clearTimeout(timer);
      // 全局 401 拦截：token 过期自动清除并广播
      if (res.status === 401 && !url.includes('/auth/')) {
        try { localStorage.removeItem('xf_auth_token'); } catch {}
        try { window.dispatchEvent(new CustomEvent('xf:session-expired')); } catch {}
      }
      return res;
    } catch (error) {
      clearTimeout(timer);
      lastError = error;
      if (error instanceof Error && error.name === 'AbortError') {
        lastError = new Error(
          timeoutMs >= TIMEOUT_PRESETS.upload
            ? '上传超时（已超过 5 分钟），请检查网络或文件大小'
            : '请求超时，请检查网络或后端服务是否运行',
        );
        break;
      }
      if (attempt >= attempts - 1 || !shouldRetryLocalBackend(url, error)) break;
      await sleep(Math.min(250 + attempt * 350, 1500));
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Unable to reach the 小枫OS local server');
}
